//#region Util Imports

import * as _ from 'underscore'

//#endregion

//#region Angular Imports

import { StateService, StateParams } from '@uirouter/angularjs'

//#endregion

//#region DevExpress Imports

import DevExpress from 'devextreme'

//#endregion

import { IUserSession, ToastHelper, LocalizationHelper, LogHelper, ConvertHelper, EventsNames, TemplateHelper, ConfigHelper, CommonHelper, DataProvider } from '../../helpers'
import { Enums, Activity } from '../../models/common'
import { OpDashboard } from '../../models/custom'

import { IMRDXDashboardSimpleOptions } from './components/dxdashboard-simple/dxdashboard-simple.controller'
import { IMRDXDashboardMultiOptions, IIMRDXDashboardMultiComponent } from './components/dxdashboard-multi/dxdashboard-multi.controller'
import { DashboardCreateEditPopupOptions } from '../dashboard/create-edit-popup/create-edit-popup.controller'
import { DescrModel, DashboardSaveModel, DashboardModel, ResponseModel } from '../api/api.models'
import { BackwardCompatibilityHelper } from '../../helpers/backward-compatibility-helper'
import { Api } from '../api/api.module'
import { MenuItem } from '../common/sidebar-menu/common.sidebar-menu.directive'
import { DashboardService } from '../dashboard/dashboard.service'
import { DashboardDestinationLevelPopupOptions, DestinationLevelModel } from '../dashboard/destination-level-popup/destination-level-popup.controller'
import { OpOperator } from '../../models/core'
import { IMRBaseControlDirectiveController } from '../common/base'

export interface DXDashboardScope extends ng.IScope {
    dxdashboardSimpleOptions: IMRDXDashboardSimpleOptions
    dxdashboardMultiOptions: IMRDXDashboardMultiOptions

    editDashboardEnable: boolean
    isEditMode: boolean
    addDashboardButtonVisible: boolean
    addDashboardButtonOptions: DevExpress.ui.dxButtonOptions
    editDashboardButtonOptions: DevExpress.ui.dxButtonOptions
    createEditDashboardOptions: DashboardCreateEditPopupOptions
    descriptions: DescrModel[]

    cancelButtonOptions: DevExpress.ui.dxButtonOptions
    saveButtonOptions: DevExpress.ui.dxButtonOptions
    isLoading: boolean
    isApplyButtonClicked: boolean

    destinationLevelPopupOptions: DashboardDestinationLevelPopupOptions
}

export class DXDashboardController extends IMRBaseControlDirectiveController {

    // Consts

    private static get LOG_PREFIX(): string { return '[SIMAX.Module.DXDashboard]' }

    // Consts End


    // Members     

    private dxdashboardMultiComponent: IIMRDXDashboardMultiComponent

    private needUpdateMenu: boolean
    private dashboard: OpDashboard
    private model: DashboardModel
    private addDashboardModel: DashboardModel
    private newDashboardModel: DashboardModel
    private editDashboardModel: DashboardModel

    private loggedUser: OpOperator
    private operatorId: number
    private loggedOperatorId: number
    private distributorId: number

    // Members End


    // Ctor

    constructor(
        private $scope: DXDashboardScope,
        private $stateParams: StateParams,
        private userSession: IUserSession,
        private api: Api,
        private $state: StateService,
        private $timeout: ng.ITimeoutService,
        private dashboardService: DashboardService,
        private $location: ng.ILocationService
    ) {
        super()
        
        this.setDashboards($stateParams['id'])
        this.setEvents()
        this.setOptions()
    }

    // Ctor End


    // OpDashboard

    private getDashboard(dashboardId: number): JQueryPromise<OpDashboard> {
        return this.userSession
            .getDataProvider().Operator
            .getDashboardConf(this.userSession.getIdLoggedOperator(), dashboardId)
            .then((dashboards: OpDashboard[]) => {
                if (_.isArray(dashboards)) {
                    return _.first(dashboards)
                }

                ToastHelper.showError(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
                LogHelper.warn(DXDashboardController.LOG_PREFIX, `No dashboard received for dashboardId: ${dashboardId}.`)

                return null
            })
            .fail((error: any) => {
                const errorMessage: string = ConvertHelper.getRequestErrorMessage(error);

                ToastHelper.showError(errorMessage || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
                LogHelper.warn(DXDashboardController.LOG_PREFIX, `Error loading dashboard for dashboardId: ${dashboardId}`, errorMessage)

                return null
            });

    }

    // OpDashboard End 


    // Options

    private setDashboards(dashboardId: number): void {
        this.$scope.addDashboardButtonVisible = this.userSession.getLoggedOperator().hasPermission(Activity.WEBSIMAX_DASHBOARD_ADD)
        this.$scope.editDashboardEnable = this.userSession.getLoggedOperator().hasPermission(Activity.WEBSIMAX_DASHBOARD_CONFIGURATION)
        this.$scope.isEditMode = false

        this.loggedUser = this.userSession.getLoggedOperator()
        this.operatorId = this.loggedUser.idOperator
        this.loggedOperatorId = this.loggedUser.idOperator
        this.distributorId = this.loggedUser.idDistributor

        this.$scope.addDashboardButtonOptions = {
            template: TemplateHelper.getIcon(ConfigHelper.Icon.ADD, this.userSession),
            hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.AddNewDashboard),
            onInitialized: (e: any) => { },
            onClick: (e: any) => {
                const opDashboard: OpDashboard = new OpDashboard()
                opDashboard.opState = Enums.OpChangeState.New
                opDashboard.fitToScreen = false
                opDashboard.keepAspectRatio = false

                this.$scope.createEditDashboardOptions = {
                    dashboardModel: opDashboard,
                    descriptions: this.getDescriptions(),
                    isNew: true,
                    onSubmitCallback: this.createEditDashboardCallback
                }
            }
        }

        this.$scope.editDashboardButtonOptions = {
            template: TemplateHelper.getIcon(ConfigHelper.Icon.EDIT, this.userSession),
            hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Edit),
            onInitialized: (e: any) => { },
            onClick: (e: any) => {
                let opDashboard: OpDashboard
                if (this.editDashboardModel) {
                    opDashboard = BackwardCompatibilityHelper.DashboardModelToOpDashboard(this.editDashboardModel)
                } else {
                    if (this.$scope.dxdashboardSimpleOptions) {
                        opDashboard = this.$scope.dxdashboardSimpleOptions.dashboard
                    } else if (this.$scope.dxdashboardMultiOptions) {
                        opDashboard = this.$scope.dxdashboardMultiOptions.dashboard
                    }
                }

                if (opDashboard != null) {
                    opDashboard.opState = Enums.OpChangeState.Modified
                }

                this.$scope.createEditDashboardOptions = {
                    dashboardModel: opDashboard,
                    descriptions: this.getDescriptions(),
                    isNew: false,
                    onSubmitCallback: this.createEditDashboardCallback
                }
            }
        }

        let id: string | number = this.$stateParams['id']
        if (!id || id == '') {
            id = 0
        }

        if (dashboardId != null) {
            this.getDashboard(dashboardId)
                .then((dashboard: OpDashboard) => {
                    this.dashboard = dashboard
                    this.model = BackwardCompatibilityHelper.opDashboardToDashboardModel(dashboard)
                    if (dashboard != null) {
                        if (dashboard.type === Enums.DashboardType.Standard) {
                            this.$state.go('dashboard', { id: dashboard.idDashboard })
                        } else {
                            if (id != dashboard.idDashboard) {
                                if (dashboard.type === Enums.DashboardType.DXDashboard || dashboard.type === Enums.DashboardType.DXDashboardMulti) {
                                    this.$state.go('dxdashboard', { id: dashboard.idDashboard })
                                }
                            }

                            switch (dashboard.type) {
                                case Enums.DashboardType.DXDashboard:
                                    this.$scope.dxdashboardSimpleOptions = {
                                        dashboard
                                    }

                                    break

                                case Enums.DashboardType.DXDashboardMulti:
                                    this.$scope.dxdashboardMultiOptions = {
                                        dashboard,
                                        onInitialized: (dashboardComponent: IIMRDXDashboardMultiComponent) => this.dxdashboardMultiComponent = dashboardComponent
                                    }

                                    break

                                default:
                                    ToastHelper.showError(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
                                    LogHelper.warn(DXDashboardController.LOG_PREFIX, `Unsupported dashboard type for idDashboard: ${dashboardId}.`)

                                    break
                            }
                        }
                    }
                })
        }
        else {
            ToastHelper.showError(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
            LogHelper.warn(DXDashboardController.LOG_PREFIX, `No dashboardId or dashboardIndex given.`)
        }
    }

    private setEvents(): void {
        this.$scope.$on(EventsNames.BEGIN_DASHBOARD_EDIT, () => {
            if (this.dxdashboardMultiComponent != null) {
                this.dxdashboardMultiComponent.toggleEditMode()
            }

            this.$scope.isEditMode = true
        })
    }

    private setOptions(): void {
        this.loggedOperatorId = this.api.login.getUser().id

        this.$scope.cancelButtonOptions = {
            text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Cancel),
            onClick: (e: any) => {
                this.$scope.isEditMode = false
                this.editDashboardModel = null
                this.addDashboardModel = null
            }
        }

        this.$scope.saveButtonOptions = {
            text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Save),
            onClick: (e: any) => {
                this.showDestinationLevelPopup()
            }
        }
    }

    // Options End

    private getDescriptions(): DescrModel[] {
        const descriptions: DescrModel[] = []
        let opDashboard: OpDashboard

        if (this.$scope.dxdashboardSimpleOptions) {
            opDashboard = this.$scope.dxdashboardSimpleOptions.dashboard
        } else if (this.$scope.dxdashboardMultiOptions) {
            opDashboard = this.$scope.dxdashboardMultiOptions.dashboard
        }

        if (opDashboard && opDashboard.descr) {
            for (let item in opDashboard.descr) {
                if (item) {
                    descriptions.push(BackwardCompatibilityHelper.OpDescrToDescrModel(opDashboard.descr[item]))
                }
            }
        }

        return descriptions
    }

    private onDeleteDashboard(opDashboard: OpDashboard): void {
        const dashboardModel = new DashboardSaveModel()
        dashboardModel.configurationSourceId = opDashboard.configurationSourceId
        dashboardModel.configurationSourceLevel = opDashboard.configurationSourceLevel
        dashboardModel.id = opDashboard.idDashboard
        dashboardModel.destinationObject = this.api.login.getUser().id

        this.api.dashboard.deleteDashboardConfiguration(dashboardModel).then(result => {
            this.$scope.$root.$broadcast(EventsNames.SHOW_LOADING_INDICATOR)
            if (!result.hasErrorCode) {
                CommonHelper.openInfoPopup(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.DashboardDeleted))
                this.dashboardService.updateSidebarMenu(this.$scope.$root, this.userSession, this.dashboard, true, this.needUpdateMenu).then(() => {
                    const filteredItems = MenuItem.getMenuItems().filter(item => item.state === 'dashboard')
                    if (filteredItems.length) {
                        const item = filteredItems[0]
                        this.$state.go(item.state, item.params)
                    }

                    DataProvider.Dashboard.refreshDashboards(this.api, true)
                })
            } else {
                this.$scope.$root.$broadcast(EventsNames.HIDE_LOADING_INDICATOR)
            }
        }).finally(() => {
            this.editDashboardModel = null
            this.addDashboardModel = null
        })
    }

    private createEditDashboardCallback = (opDashboard: OpDashboard): void => {
        if (opDashboard.opState === Enums.OpChangeState.New) {
            this.addDashboardModel = BackwardCompatibilityHelper.opDashboardToDashboardModel(opDashboard)
            this.newDashboardModel = jQuery.extend(true, {}, this.addDashboardModel)
            this.showDestinationLevelPopup()
            this.addDashboardModel = null
        }
        if (opDashboard.opState === Enums.OpChangeState.Modified) {
            this.editDashboardModel = BackwardCompatibilityHelper.opDashboardToDashboardModel(opDashboard)
        }
        if (opDashboard.opState === Enums.OpChangeState.Delete) {
            this.onDeleteDashboard(opDashboard)
        }
    }

    private showDestinationLevelPopup = (): void => {
        this.$scope.destinationLevelPopupOptions = {
            distributorId: this.distributorId,
            operatorId: this.operatorId,
            onSubmitCallback: this.saveDashboard
        }
    }

    private saveDashboard = (destination: DestinationLevelModel): void => {
        this.$scope.isApplyButtonClicked = true
        this.$scope.isLoading = true

        const dashboardModel: DashboardSaveModel = new DashboardSaveModel()
        dashboardModel.destinationLevel = destination.destination
        dashboardModel.destinationObject = destination.id
        dashboardModel.id = this.dashboard.idDashboard
        this.$scope.$root.$broadcast(EventsNames.SHOW_LOADING_INDICATOR)
        if (this.newDashboardModel) {
            _.assign(dashboardModel, this.newDashboardModel)
            dashboardModel.rows = []
            dashboardModel.id = 0
            dashboardModel.fitToScreen = !!dashboardModel.fitToScreen
            dashboardModel.keepAspectRatio = !!dashboardModel.keepAspectRatio

            this.api.dashboard.saveDashboardConfiguration(dashboardModel)
                .then((result: ResponseModel) => {
                    this.$scope.isEditMode = false

                    if (!result.hasErrorCode) {
                        this.newDashboardModel = null
                        ToastHelper.showSuccess(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ConfigurationSaved))

                        this.dashboardService.updateSidebarMenu(this.$scope.$root, this.userSession, this.dashboard, true, this.needUpdateMenu).then(() => {
                            DataProvider.Dashboard.refreshDashboards(this.api, true)
                        })
                    } else {
                        this.$scope.$root.$broadcast(EventsNames.HIDE_LOADING_INDICATOR)
                    }
                })
                .finally(() => {
                    this.$scope.isLoading = false
                })
        } else {
            _.assign(dashboardModel, BackwardCompatibilityHelper.opDashboardToDashboardModel(this.dashboard))
            dashboardModel.opState = CommonHelper.ChangeState.Modified
            dashboardModel.descriptions = null

            DataProvider.Dashboard.getDashboards().then(dashboards => {
                this.api.dashboard.saveDashboardConfiguration(dashboardModel).then(result => {
                    this.$scope.isEditMode = false

                    if (!result.hasErrorCode) {
                        CommonHelper.openInfoPopup(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ConfigurationSaved))
                    }

                    if (this.needUpdateMenu) {
                        this.dashboardService.updateSidebarMenu(this.$scope.$root, this.userSession, this.dashboard, true, this.needUpdateMenu).then(() => {
                            DataProvider.Dashboard.refreshDashboards(this.api, true)
                        })
                        this.needUpdateMenu = false
                    } else {
                        DataProvider.Dashboard.refreshDashboards(this.api, true)
                        this.$scope.$root.$broadcast(EventsNames.HIDE_LOADING_INDICATOR)
                    }
                    this.setDashboards(this.$stateParams['id'])
                }).finally(() => {
                    this.$scope.isLoading = false
                    this.$scope.isApplyButtonClicked = false
                })
            })
        }

    }
}
